import { Alert, Anchor, Button, Group, PasswordInput, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useState } from "react";
import { Link } from "react-router-dom";

import { type ApiError, errorMessage, isApiError } from "@/api";

import { useSession } from "../session";
import { AuthCard } from "./AuthCard";

function loginError(e: unknown): string {
  if (!isApiError(e)) return errorMessage(e);
  switch (e.code) {
    case "INVALID_CREDENTIALS":
      return "Email or password is incorrect.";
    case "LOCKED_OUT":
      return "Too many failed attempts: the account is temporarily locked. Try again later.";
    case "ACCOUNT_BLOCKED":
      return "Your account is blocked. Contact your administrator.";
    default:
      return errorMessage(e);
  }
}

/** /login: password step, then the 2FA code step (spec §3.1). Redirects are done by `PublicOnly`. */
export function LoginPage() {
  const { state, login, verifyTotp, cancelLogin } = useSession();
  const creds = useForm({ initialValues: { email: "", password: "" } });
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const notice = state.status === "anonymous" ? state.notice : undefined;

  const submitPassword = creds.onSubmit(async ({ email, password }) => {
    setBusy(true);
    setError(null);
    try {
      await login(email.trim(), password);
      setCode("");
    } catch (e) {
      setError(loginError(e));
    } finally {
      setBusy(false);
    }
  });

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setCodeError(null);
    try {
      await verifyTotp(code.trim().replace(/\s+/g, ""));
    } catch (err) {
      if (isApiError(err, "INVALID_TOTP")) {
        // The pending token stays valid; after 5 wrong codes the API answers PENDING_EXPIRED instead.
        setCodeError("That code is not valid. Check your authenticator and try again.");
        setCode("");
      } else if (!isApiError(err, "PENDING_EXPIRED")) {
        setCodeError(loginError(err as ApiError));
      }
    } finally {
      setBusy(false);
    }
  };

  if (state.status === "password_ok_needs_totp") {
    return (
      <AuthCard
        title="Two-factor authentication"
        subtitle="Enter the 6-digit code from your authenticator app, or one of your backup codes."
      >
        <form onSubmit={submitCode}>
          <Stack>
            <TextInput
              label="Code"
              value={code}
              onChange={(e) => setCode(e.currentTarget.value)}
              error={codeError}
              autoComplete="one-time-code"
              autoFocus
              required
              data-testid="totp-code"
            />
            <Button type="submit" loading={busy} data-testid="totp-submit">
              Verify
            </Button>
            <Anchor component="button" type="button" size="sm" onClick={cancelLogin}>
              Use a different account
            </Anchor>
          </Stack>
        </form>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Sign in">
      <form onSubmit={submitPassword}>
        <Stack>
          {notice && (
            <Alert color="yellow" data-testid="login-notice">
              {notice}
            </Alert>
          )}
          <TextInput
            label="Email"
            type="email"
            autoComplete="username"
            required
            autoFocus
            {...creds.getInputProps("email")}
            data-testid="login-email"
          />
          <PasswordInput
            label="Password"
            autoComplete="current-password"
            required
            {...creds.getInputProps("password")}
            data-testid="login-password"
          />
          {error && (
            <Alert color="red" data-testid="login-error">
              {error}
            </Alert>
          )}
          <Button type="submit" loading={busy} data-testid="login-submit">
            Continue
          </Button>
          <Group justify="center">
            <Anchor component={Link} to="/forgot" size="sm">
              Forgot your password?
            </Anchor>
          </Group>
        </Stack>
      </form>
    </AuthCard>
  );
}
